import type {
  ReactElement,
  RefAttributes,
} from 'react';

import ReactSelect from 'react-select';
import type {
  GroupBase,
  Props as ReactSelectProps,
  SelectInstance,
} from 'react-select';

import ReactSelectCreatable from 'react-select/creatable';
import type {
  CreatableProps as ReactSelectCreatableProps,
} from 'react-select/creatable';

import { AsyncPaginate, withAsyncPaginate } from 'react-select-async-paginate';
import type {
  AsyncPaginateProps,
  ComponentProps,
  UseAsyncPaginateParams,
} from 'react-select-async-paginate';

import { SelectFetch } from 'react-select-fetch';
import type {
  SelectFetchProps,
} from 'react-select-fetch';

import type {
  ExtensionProps,
} from './types';

export type ReactSelectExtendedProps<
Option,
IsMulti extends boolean,
Group extends GroupBase<Option>,
> =
  & ReactSelectProps<Option, IsMulti, Group>
  & ExtensionProps;

export type ReactSelectExtendedType = <
Option,
IsMulti extends boolean,
Group extends GroupBase<Option>,
>(
  props: ReactSelectExtendedProps<Option, IsMulti, Group>
  & RefAttributes<SelectInstance<Option, IsMulti, Group>>,
) => ReactElement;

export const ReactSelectExtended = ReactSelect as ReactSelectExtendedType;

export type ReactSelectCreatableExtendedProps<
Option,
IsMulti extends boolean,
Group extends GroupBase<Option>,
> =
  & ReactSelectCreatableProps<Option, IsMulti, Group>
  & ExtensionProps;

export type ReactSelectCreatableExtendedType = <
Option,
IsMulti extends boolean,
Group extends GroupBase<Option>,
>(
  props: ReactSelectCreatableExtendedProps<Option, IsMulti, Group>
  & RefAttributes<SelectInstance<Option, IsMulti, Group>>,
) => ReactElement;

// eslint-disable-next-line max-len
export const ReactSelectCreatableExtended = ReactSelectCreatable as ReactSelectCreatableExtendedType;

export type AsyncPaginateExtendedProps<
Option,
IsMulti extends boolean,
Additional,
Group extends GroupBase<Option>,
> =
  & AsyncPaginateProps<Option, Group, Additional, IsMulti>
  & ExtensionProps;

export type AsyncPaginateExtendedType = <
Option,
IsMulti extends boolean,
Additional,
Group extends GroupBase<Option>,
>(
  props: AsyncPaginateExtendedProps<Option, IsMulti, Additional, Group>,
) => ReactElement;

export const AsyncPaginateExtended = AsyncPaginate as AsyncPaginateExtendedType;

export type SelectFetchExtendedProps<
Option = unknown,
IsMulti extends boolean = false,
Group extends GroupBase<Option> = GroupBase<Option>,
> =
  & SelectFetchProps<Option, Group, IsMulti>
  & ExtensionProps;

export type SelectFetchExtendedType = <
Option = unknown,
IsMulti extends boolean = false,
Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: SelectFetchExtendedProps<Option, IsMulti, Group>,
) => ReactElement;

export const SelectFetchExtended = SelectFetch as SelectFetchExtendedType;

export type CreatableAsyncPaginateExtendedProps<
Option,
Group extends GroupBase<Option>,
Additional,
IsMulti extends boolean,
> =
  & ReactSelectCreatableExtendedProps<Option, IsMulti, Group>
  & UseAsyncPaginateParams<Option, Group, Additional>
  & ComponentProps<Option, Group, IsMulti>;

type CreatableAsyncPaginateExtendedType = <
Option,
Group extends GroupBase<Option>,
Additional,
IsMulti extends boolean,
>(props: CreatableAsyncPaginateExtendedProps<Option, Group, Additional, IsMulti>) => ReactElement;

// eslint-disable-next-line max-len
export const CreatableAsyncPaginateExtended = withAsyncPaginate(ReactSelectCreatable) as CreatableAsyncPaginateExtendedType;
